import { jsx as _jsx, jsxs as _jsxs } from "preact/jsx-runtime";
/* eslint-disable @typescript-eslint/ban-ts-comment */
import { useMemo, useState, Suspense, useCallback, useContext, useRef, } from 'preact/compat';
import { FormProvider, useForm } from 'react-hook-form';
import convertDataAttributes from '@widgets/shared/convertDataAttributes';
import LoadingSpinner from '@widgets/shared/components/LoadingSpinner';
import { NewsletterCTX, newsletterRegistrationDefaults, } from '@widgets/NewsletterRegistration/context/Options';
import { newsletterFormSchema } from '@widgets/NewsletterRegistration/validation/schema';
import { yupResolver } from '@hookform/resolvers/yup';
import TextInput from '@widgets/shared/components/form-controls/TextInput';
import API from '@widgets/shared/API/connection';
import FormNotices from '@widgets/shared/components/FormNotices';
function Form() {
    const formRef = useRef(null);
    const { backgroundImageUrl, title, copy } = useContext(NewsletterCTX);
    const [isRequesting, setIsRequesting] = useState(false);
    const [complete, setComplete] = useState(false);
    const [successTitle, setSuccessTitle] = useState(undefined);
    const [successCopy, setSuccessCopy] = useState(undefined);
    const [errorMessage, setErrorMessage] = useState(undefined);
    const form = useForm({
        mode: 'onChange',
        resolver: yupResolver(newsletterFormSchema),
        defaultValues: {
            email: '',
        },
    });
    const { handleSubmit, watch, formState: { isValid }, setError, } = form;
    const email = watch('email');
    const handleSubmitEvent = useCallback(() => {
        setIsRequesting(true);
        API.newsletterRegistration(email)
            .then((res) => {
            const { success, errors, message, successCopy: remoteSuccessCopy, successTitle: remoteSuccessTitle, } = res;
            if (success) {
                setComplete(true);
                setSuccessTitle(remoteSuccessTitle ?? 'Thank you text here');
                setSuccessCopy(remoteSuccessCopy ?? '');
            }
            else {
                errors.forEach((error) => {
                    if (error.field) {
                        // @ts-ignore ignoring as error.field is probably email and shouldn't cause issues even if it isn't...
                        setError(error.field, { message: error.message }, { shouldFocus: true });
                    }
                    if (message) {
                        setErrorMessage(message);
                    }
                });
            }
        })
            .finally(() => {
            setIsRequesting(false);
        });
    }, [email, setError]);
    const handleNoticeClose = useCallback(() => {
        setErrorMessage(undefined);
    }, []);
    const canSubmit = useMemo(() => isValid && !isRequesting, [isValid, isRequesting]);
    const containerContent = useMemo(() => {
        if (complete && successTitle && successCopy) {
            return (_jsxs("div", { className: "section__form-wrapper success", children: [successTitle && (_jsx("h2", { className: "section__title", children: successTitle })), successCopy && (_jsx("p", { className: "section__copy", children: successCopy }))] }));
        }
        return (_jsxs("div", { className: "section__form-wrapper", children: [errorMessage && (_jsx("div", { style: { marginBottom: '1rem' }, children: _jsx(FormNotices, { message: errorMessage, type: "error", handleClose: handleNoticeClose, closeTimer: 12000 }) })), title && (_jsx("h2", { className: "section__title", children: title })), copy && (_jsx("p", { className: "section__copy", children: copy })), _jsx("form", { method: "POST", ref: formRef, onSubmit: handleSubmit(handleSubmitEvent), noValidate: true, className: "form", children: _jsxs("div", { className: "form__fields", children: [_jsx(TextInput, { inputAttrs: {
                                    id: 'email',
                                    name: 'email',
                                    type: 'email',
                                    placeholder: 'Enter your email',
                                }, labelAttrs: {
                                    htmlFor: 'email',
                                    text: 'Enter your email',
                                    isFieldRequired: true,
                                    hidden: true,
                                } }), _jsx("button", { type: "submit", className: "btn", disabled: !canSubmit, children: "Sign up!" })] }) })] }));
    }, [
        complete,
        successTitle,
        successCopy,
        errorMessage,
        handleNoticeClose,
        title,
        copy,
        handleSubmit,
        handleSubmitEvent,
        canSubmit,
    ]);
    return (_jsx(FormProvider, { ...form, children: _jsx("div", { className: "container", children: _jsx("div", { className: "section__content", style: {
                    backgroundImage: backgroundImageUrl !== undefined
                        ? `url(${backgroundImageUrl})`
                        : 'url(https://via.placehold.co/2480x1000/png)',
                }, children: containerContent }) }) }));
}
function NewsletterRegistration(props) {
    const { dataset } = props;
    const formattedAttributes = convertDataAttributes(dataset ?? {});
    const componentConfig = useMemo(() => ({
        ...newsletterRegistrationDefaults,
        ...formattedAttributes,
    }), [formattedAttributes]);
    return (_jsx(Suspense, { fallback: LoadingSpinner, children: _jsx(NewsletterCTX.Provider, { value: componentConfig, children: _jsx(Form, {}) }) }));
}
export default NewsletterRegistration;
